import { useCallback, useEffect, useRef, useState } from 'react';

import {
  aPantalla,
  desdePantalla,
  type PuntoEsferico,
} from './geometriaPanorama';

/**
 * Un visor de panorámicas equirectangulares.
 *
 * <p><b>Hay dos copias de este fichero</b>: `apps/portal/src/componentes/` y
 * `apps/panel/src/ui/`. Son idénticas salvo la directiva `'use client'` del
 * portal y la ruta del importe de la geometría. No es un descuido: el panel y el
 * portal son dos paquetes de npm independientes, sin espacio de trabajo
 * compartido, y montarlo para compartir un componente sería cambiar el sistema
 * de compilación de los dos. Si se toca uno hay que tocar el otro; están
 * escritas para poder compararlas línea a línea.
 *
 * <p>Está escrito a mano y no con una librería, y la razón es el tamaño: las
 * que hay —pannellum, photo-sphere-viewer— traen su propio motor 3D y pesan
 * entre 80 KB y más de medio mega. Esto son cien líneas y un par de shaders,
 * porque una equirectangular no necesita geometría: se pinta un rectángulo que
 * cubre la pantalla y, para cada píxel, el shader calcula hacia dónde mira y
 * toma el color de la imagen. No hay esfera, no hay mallas y no hay matrices de
 * modelo.
 *
 * <p>Se usa para dos cosas distintas con el mismo código: en el panel, para ver
 * la panorámica y **colocar** los puntos de paso pulsando donde van; y en el
 * portal, para recorrerla. De ahí `alPulsar`, que devuelve el yaw y el pitch de
 * donde se pulsó.
 */

const VERTEX = `
attribute vec2 posicion;
varying vec2 uv;
void main() {
  uv = posicion;
  gl_Position = vec4(posicion, 0.0, 1.0);
}
`;

/*
 * Para cada pixel: se arma el rayo que sale de la camara, se gira por el yaw y
 * el pitch, y se convierte a coordenadas de la equirectangular. `PI` y `TAU`
 * van a mano porque GLSL 1.0 no las trae.
 */
const FRAGMENT = `
precision highp float;
varying vec2 uv;
uniform sampler2D imagen;
uniform vec2 tamano;
uniform float yaw;
uniform float pitch;
uniform float tanMitadFov;

const float PI = 3.14159265359;
const float TAU = 6.28318530718;

void main() {
  float aspecto = tamano.x / tamano.y;
  vec3 rayo = normalize(vec3(uv.x * tanMitadFov * aspecto, uv.y * tanMitadFov, -1.0));

  float cp = cos(pitch), sp = sin(pitch);
  rayo = vec3(rayo.x, rayo.y * cp - rayo.z * sp, rayo.y * sp + rayo.z * cp);

  float cy = cos(yaw), sy = sin(yaw);
  rayo = vec3(rayo.x * cy + rayo.z * sy, rayo.y, -rayo.x * sy + rayo.z * cy);

  float lon = atan(rayo.x, -rayo.z);
  float lat = asin(clamp(rayo.y, -1.0, 1.0));

  gl_FragColor = texture2D(imagen, vec2(lon / TAU + 0.5, 0.5 - lat / PI));
}
`;

export type { PuntoEsferico };

export interface HotspotVisible extends PuntoEsferico {
  id: string;
  titulo: string;
  /** Lo que se pinta dentro del marcador. */
  etiqueta: string;
}

function compilar(gl: WebGLRenderingContext, tipo: number, fuente: string): WebGLShader {
  const shader = gl.createShader(tipo);
  if (!shader) throw new Error('sin shader');
  gl.shaderSource(shader, fuente);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) ?? 'shader no compila');
  }
  return shader;
}

/**
 * La imagen, reducida si la tarjeta gráfica no la admite.
 *
 * <p>Las panorámicas se sirven a 6000 px de ancho y muchas GPU de móvil no
 * pasan de 4096: subir la textura tal cual falla en silencio y deja el visor
 * negro. Se consulta el límite real y, si hace falta, se redibuja en un lienzo
 * al tamaño que sí entra.
 */
function aTextura(gl: WebGLRenderingContext, imagen: HTMLImageElement): TexImageSource {
  const limite = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  if (imagen.width <= limite) return imagen;

  const escala = limite / imagen.width;
  const lienzo = document.createElement('canvas');
  lienzo.width = limite;
  lienzo.height = Math.max(1, Math.round(imagen.height * escala));
  lienzo.getContext('2d')?.drawImage(imagen, 0, 0, lienzo.width, lienzo.height);
  return lienzo;
}

export default function Panorama({
  src,
  alPulsar,
  hotspots = [],
  alPulsarHotspot,
  alMirar,
  yawInicial = 0,
  pitchInicial = 0,
  alto = 420,
}: {
  src: string;
  /** Devuelve el yaw y el pitch de donde se pulsó, en grados. */
  alPulsar?: (punto: PuntoEsferico) => void;
  hotspots?: HotspotVisible[];
  alPulsarHotspot?: (id: string) => void;
  /**
   * Hacia dónde está mirando, al terminar de arrastrar.
   *
   * <p>Lo necesita el panel para poder guardar «la vista con la que se entra a
   * esta escena»: sin esto, ese botón guardaba el valor que ya estaba y no lo
   * que se estaba viendo.
   */
  alMirar?: (punto: PuntoEsferico) => void;
  yawInicial?: number;
  pitchInicial?: number;
  /**
   * Alto de la caja. Un número son píxeles; `'100%'` llena al padre, que es lo
   * que hace falta cuando el visor va a pantalla completa.
   */
  alto?: number | string;
}) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const contenedor = useRef<HTMLDivElement>(null);
  const camara = useRef({ yaw: (yawInicial * Math.PI) / 180, pitch: (pitchInicial * Math.PI) / 180, fov: (75 * Math.PI) / 180 });
  const arrastre = useRef<{ x: number; y: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);
  /** Donde cae cada hotspot en la pantalla. Se recalcula al mover la vista. */
  const [posiciones, setPosiciones] = useState<Record<string, { x: number; y: number }>>({});

  /*
   * La proyección y su inversa viven en `geometriaPanorama`, con prueba propia:
   * si las dos no son inversas exactas, un punto de paso queda unos grados
   * corrido de donde se pulsó y nada falla —solo sale mal, y con el visor
   * delante cuesta mucho darse cuenta—.
   */
  const proyectar = useCallback(
    (p: PuntoEsferico, ancho: number, altoPx: number) =>
      aPantalla(p, ancho, altoPx, camara.current),
    [],
  );

  useEffect(() => {
    const nodo = lienzo.current;
    if (!nodo) return;

    const gl = nodo.getContext('webgl', { antialias: false });
    if (!gl) {
      setError('Este navegador no puede mostrar panorámicas.');
      return;
    }

    let programa: WebGLProgram | null = null;
    let textura: WebGLTexture | null = null;
    let vivo = true;

    try {
      programa = gl.createProgram();
      if (!programa) throw new Error('sin programa');
      gl.attachShader(programa, compilar(gl, gl.VERTEX_SHADER, VERTEX));
      gl.attachShader(programa, compilar(gl, gl.FRAGMENT_SHADER, FRAGMENT));
      gl.linkProgram(programa);
      if (!gl.getProgramParameter(programa, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(programa) ?? 'no enlaza');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'no se pudo preparar el visor');
      return;
    }

    gl.useProgram(programa);

    // El rectangulo que cubre la pantalla. Dos triangulos y se acabo.
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const posicion = gl.getAttribLocation(programa, 'posicion');
    gl.enableVertexAttribArray(posicion);
    gl.vertexAttribPointer(posicion, 2, gl.FLOAT, false, 0, 0);

    const uYaw = gl.getUniformLocation(programa, 'yaw');
    const uPitch = gl.getUniformLocation(programa, 'pitch');
    const uFov = gl.getUniformLocation(programa, 'tanMitadFov');
    const uTamano = gl.getUniformLocation(programa, 'tamano');

    const imagen = new Image();
    imagen.crossOrigin = 'anonymous';

    const pintar = () => {
      if (!vivo || !textura) return;
      const escala = Math.min(window.devicePixelRatio || 1, 2);
      const ancho = Math.round(nodo.clientWidth * escala);
      const altoReal = Math.round(nodo.clientHeight * escala);
      if (nodo.width !== ancho || nodo.height !== altoReal) {
        nodo.width = ancho;
        nodo.height = altoReal;
      }
      gl.viewport(0, 0, ancho, altoReal);
      gl.uniform1f(uYaw, camara.current.yaw);
      gl.uniform1f(uPitch, camara.current.pitch);
      gl.uniform1f(uFov, Math.tan(camara.current.fov / 2));
      gl.uniform2f(uTamano, ancho, altoReal);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      const siguientes: Record<string, { x: number; y: number }> = {};
      for (const h of hotspots) {
        const p = proyectar(h, nodo.clientWidth, nodo.clientHeight);
        if (p) siguientes[h.id] = p;
      }
      setPosiciones(siguientes);
    };

    imagen.onload = () => {
      if (!vivo) return;
      textura = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, textura);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, aTextura(gl, imagen));
      setListo(true);
      pintar();
    };
    imagen.onerror = () => setError('No se pudo cargar la panorámica.');
    imagen.src = src;

    // Se repinta al mover, no en bucle: una panorámica quieta no necesita
    // sesenta cuadros por segundo, y en un portatil eso es el ventilador.
    const alMover = () => requestAnimationFrame(pintar);
    const observador = new ResizeObserver(alMover);
    observador.observe(nodo);
    (nodo as HTMLCanvasElement & { __pintar?: () => void }).__pintar = alMover;

    return () => {
      vivo = false;
      observador.disconnect();
      if (textura) gl.deleteTexture(textura);
      if (programa) gl.deleteProgram(programa);
    };
  }, [src, hotspots, proyectar]);

  const repintar = () => {
    const nodo = lienzo.current as (HTMLCanvasElement & { __pintar?: () => void }) | null;
    nodo?.__pintar?.();
  };

  const alBajar = (e: React.PointerEvent) => {
    arrastre.current = { x: e.clientX, y: e.clientY };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const alArrastrar = (e: React.PointerEvent) => {
    const desde = arrastre.current;
    if (!desde) return;
    const nodo = lienzo.current;
    if (!nodo) return;

    // Un pixel de arrastre mueve lo que ese pixel abarca del campo de vision:
    // asi el giro sigue al dedo en vez de a una constante inventada.
    const porPixel = camara.current.fov / nodo.clientHeight;

    /*
     * El signo del horizontal es `+`, y estuvo en `-`.
     *
     * La imagen tiene que seguir al dedo: arrastrar a la derecha revela lo que
     * habia a la izquierda, como al mover una foto. Medido contra el shader en
     * un navegador real, la columna de la panoramica que se muestrea baja
     * cuando el yaw sube --yaw 0 muestrea u=0.50, yaw 90 muestrea u=0.25-- asi
     * que para mirar a la izquierda hay que SUMAR al yaw.
     *
     * Con el signo al reves la imagen se movia en contra del dedo en horizontal
     * y a favor en vertical, que es lo peor de los dos mundos. El vertical ya
     * estaba bien: arrastrar hacia abajo sube el pitch y se mira arriba.
     */
    camara.current.yaw += (e.clientX - desde.x) * porPixel;
    camara.current.pitch = Math.max(
      -Math.PI / 2 + 0.01,
      Math.min(Math.PI / 2 - 0.01, camara.current.pitch + (e.clientY - desde.y) * porPixel),
    );
    arrastre.current = { x: e.clientX, y: e.clientY };
    repintar();
  };

  const alSoltar = (e: React.PointerEvent) => {
    const desde = arrastre.current;
    arrastre.current = null;

    alMirar?.({
      yaw: (camara.current.yaw * 180) / Math.PI,
      pitch: (camara.current.pitch * 180) / Math.PI,
    });

    if (!desde || !alPulsar) return;

    // Solo cuenta como pulsacion si casi no se movio: si no, colocar un punto
    // de paso seria imposible sin mover la vista por accidente.
    if (Math.abs(e.clientX - desde.x) > 4 || Math.abs(e.clientY - desde.y) > 4) return;

    const nodo = lienzo.current;
    if (!nodo) return;
    const caja = nodo.getBoundingClientRect();

    alPulsar(
      desdePantalla(
        e.clientX - caja.left,
        e.clientY - caja.top,
        caja.width,
        caja.height,
        camara.current,
      ),
    );
  };

  const alRodar = (e: React.WheelEvent) => {
    e.preventDefault();
    const fov = camara.current.fov * (e.deltaY > 0 ? 1.08 : 0.92);
    camara.current.fov = Math.max((25 * Math.PI) / 180, Math.min((110 * Math.PI) / 180, fov));
    repintar();
  };

  return (
    <div
      ref={contenedor}
      className="panorama"
      style={{ height: alto }}
      onWheel={alRodar}
    >
      <canvas
        ref={lienzo}
        className="panorama__lienzo"
        onPointerDown={alBajar}
        onPointerMove={alArrastrar}
        onPointerUp={alSoltar}
        onPointerCancel={() => {
          arrastre.current = null;
        }}
      />

      {hotspots.map((h) => {
        const p = posiciones[h.id];
        if (!p) return null;
        return (
          <button
            key={h.id}
            type="button"
            className="panorama__hotspot"
            style={{ left: p.x, top: p.y }}
            title={h.titulo}
            onClick={(e) => {
              e.stopPropagation();
              alPulsarHotspot?.(h.id);
            }}
          >
            {h.etiqueta}
          </button>
        );
      })}

      {error && <p className="panorama__aviso">{error}</p>}
      {!listo && !error && <p className="panorama__aviso">Cargando la panorámica…</p>}
      {listo && !error && alPulsar && (
        <p className="panorama__pista">Arrastra para mirar · pulsa para colocar el punto</p>
      )}
    </div>
  );
}
